import type { RefreshToken } from "./types";

const KEYS = {
  ACCESS_TOKEN: "3t-bridge:auth:access-token",
  REFRESH_TOKEN: "3t-bridge:auth:refresh-token",
  ID_TOKEN: "3t-bridge:auth:id-token",
  REDIRECT_LOCATION: "3t-bridge:auth:redirect-location",
  PROVIDER_ID: "3t-bridge:auth:provider-id",
} as const;

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Best effort — storage may be unavailable (private mode, quota exceeded)
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Best effort — don't throw on cleanup
  }
}

export const ACCESS_TOKEN_KEY = KEYS.ACCESS_TOKEN;

export const tokenStore = {
  getAccessToken: () => safeGet(KEYS.ACCESS_TOKEN),
  setAccessToken: (token: string) => safeSet(KEYS.ACCESS_TOKEN, token),
  clearAccessToken: () => safeRemove(KEYS.ACCESS_TOKEN),

  // Branded type cast is compile-time only — no runtime validation
  getRefreshToken: (): RefreshToken | null =>
    safeGet(KEYS.REFRESH_TOKEN) as RefreshToken | null,
  setRefreshToken: (token: string) => safeSet(KEYS.REFRESH_TOKEN, token),
  clearRefreshToken: () => safeRemove(KEYS.REFRESH_TOKEN),

  getIdToken: () => safeGet(KEYS.ID_TOKEN),
  setIdToken: (token: string) => safeSet(KEYS.ID_TOKEN, token),
  clearIdToken: () => safeRemove(KEYS.ID_TOKEN),

  getRedirectLocation: (): string => {
    const stored = safeGet(KEYS.REDIRECT_LOCATION);
    safeRemove(KEYS.REDIRECT_LOCATION);
    // Sanitize to relative path to prevent open-redirect
    const location = stored ?? "/";
    if (!location.startsWith("/") || location.startsWith("//")) return "/";
    if (/[\\:]/.test(location)) return "/";
    try {
      const decoded = decodeURIComponent(location);
      if (decoded.startsWith("//") || /[\\:]/.test(decoded)) return "/";
    } catch {
      return "/";
    }
    return location;
  },
  setRedirectLocation: (path: string) => safeSet(KEYS.REDIRECT_LOCATION, path),

  getProviderId: () => safeGet(KEYS.PROVIDER_ID),
  setProviderId: (id: string) => safeSet(KEYS.PROVIDER_ID, id),
  clearProviderId: () => safeRemove(KEYS.PROVIDER_ID),

  clearAll: () => {
    safeRemove(KEYS.ACCESS_TOKEN);
    safeRemove(KEYS.REFRESH_TOKEN);
    safeRemove(KEYS.ID_TOKEN);
    safeRemove(KEYS.REDIRECT_LOCATION);
    safeRemove(KEYS.PROVIDER_ID);
  },
};
